import { useMemo, useRef, useEffect, type CSSProperties } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, usePropertyField, useVirtualTours } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// Builds the bedrooms portion of the subtitle. Rather than showing a terse
// range (e.g. "Studio–2"), which reads ambiguously, we enumerate every
// bedroom type in the span as a comma list ("Studio, 1, 2"). The structured
// `property_bedrooms_min` / `property_bedrooms_max` fields drive the span;
// when those are absent we parse the pre-formatted `property_bedrooms_range`
// string instead, and only as a last resort emit the raw range field token
// (resolved/sanitized by the runtime). The enumerated output is plain literal
// text built from already-resolved field values — no unsanitized markup.
const fieldToken = (field: string) =>
  `<span data-type="field-token" data-field="${field}"></span>`;

function toTrimmed(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (value == null) return '';
  return String(value).trim();
}

// Parses a single bedroom count. "Studio"/"std"/"0" → 0; numeric strings →
// their integer; anything non-numeric → null (unusable).
function parseBedCount(value: unknown): number | null {
  const t = toTrimmed(value).toLowerCase();
  if (!t) return null;
  if (t === 'studio' || t === 'std') return 0;
  const n = parseInt(t, 10);
  return Number.isNaN(n) ? null : n;
}

// Parses a pre-formatted range string like "Studio - 2" or "1–3" into a
// [lo, hi] pair. Returns null when no bedroom counts can be recovered.
function parseBedRange(value: unknown): [number, number] | null {
  const t = toTrimmed(value);
  if (!t) return null;
  const parts = t.split(/\s*(?:-|–|—|to)\s*/i).filter(Boolean);
  if (parts.length >= 2) {
    const a = parseBedCount(parts[0]);
    const b = parseBedCount(parts[parts.length - 1]);
    if (a !== null && b !== null) return [Math.min(a, b), Math.max(a, b)];
  }
  const single = parseBedCount(t);
  return single === null ? null : [single, single];
}

function bedCountLabel(n: number): string {
  return n <= 0 ? 'Studio' : String(n);
}

function buildBedroomsHtml(
  range: unknown,
  min: unknown,
  max: unknown,
): string {
  let lo = parseBedCount(min);
  let hi = parseBedCount(max);
  // Backfill missing bounds from the pre-formatted range string.
  if (lo === null || hi === null) {
    const parsed = parseBedRange(range);
    if (parsed) {
      if (lo === null) lo = parsed[0];
      if (hi === null) hi = parsed[1];
    }
  }
  if (lo !== null && hi !== null) {
    const a = Math.min(lo, hi);
    const b = Math.max(lo, hi);
    const parts: string[] = [];
    // Enumerate each bedroom type; cap the span defensively against bad data.
    for (let n = a; n <= b && parts.length < 12; n++) parts.push(bedCountLabel(n));
    return parts.join(', ');
  }
  if (lo !== null) return bedCountLabel(lo);
  if (hi !== null) return bedCountLabel(hi);
  // Nothing parseable — fall back to the raw range field token.
  return fieldToken('property_bedrooms_range');
}

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Media',
  fields: {
    image: {
      label: 'Image or Video',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

/**
 * Detects whether a media URL points at a video file rather than an image.
 * The background slot accepts a single multihub media URL that may resolve to
 * either an image or a video, so we sniff the extension (ignoring any query
 * string) to pick the right element.
 */
function isVideoUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const pathname = url.split(/[?#]/)[0].toLowerCase();
  return /\.(mp4|webm|mov|m4v|ogv|ogg)$/.test(pathname);
}

/**
 * Picks the first property virtual tour that resolves to a directly playable
 * video file — the "cinemagraph" source. Many virtual tours are interactive
 * embeds (Matterport, YouTube watch pages, etc.) that can't be dropped into a
 * background <video>; we only accept tours whose URL is an actual video file.
 * Returns `null` when the property has no usable looping clip.
 */
function pickCinemagraphUrl(
  tours: Array<Record<string, unknown>>,
): string | null {
  for (const tour of tours) {
    const url = tour?.virtual_tour_url as string | undefined;
    if (isVideoUrl(url)) return url as string;
  }
  return null;
}

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);

  // Whatever looping clip the property happens to ship — used as the hero
  // cinemagraph background when present.
  const tours = useVirtualTours() as Array<Record<string, unknown>>;
  const cinemagraphUrl = useMemo(() => pickCinemagraphUrl(tours), [tours]);

  // A property-level custom "featured video" (set in the property data, not the
  // theme editor). When present it stands in for the featured image as the hero
  // background; we fall back to the featured image whenever it's absent.
  const customFeaturedVideoRaw = usePropertyField('custom_property_featured_video');
  const customFeaturedVideo =
    typeof customFeaturedVideoRaw === 'string' && customFeaturedVideoRaw.trim()
      ? customFeaturedVideoRaw.trim()
      : null;

  // Subtitle default. The bedrooms portion enumerates each bedroom type
  // ("Studio, 1, 2") from property_bedrooms_min/max, parsing
  // property_bedrooms_range as a fallback when those structured bounds are
  // absent.
  const bedroomsRange = usePropertyField('property_bedrooms_range');
  const bedroomsMin = usePropertyField('property_bedrooms_min');
  const bedroomsMax = usePropertyField('property_bedrooms_max');
  const subtitleDefault = useMemo(() => {
    const bedrooms = buildBedroomsHtml(bedroomsRange, bedroomsMin, bedroomsMax);
    return `<p>${bedrooms} Bedroom Apartments For Rent in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>`;
  }, [bedroomsRange, bedroomsMin, bedroomsMax]);

  // Resolve the effective background. An editor-set video in the slot always
  // wins; otherwise the property's custom featured video takes precedence over
  // the featured image; otherwise fall back to the cinemagraph clip; otherwise
  // render the still featured image with a Ken Burns drift.
  const slotIsVideo = isVideoUrl(bgImage.image);
  const videoSrc = slotIsVideo
    ? bgImage.image
    : customFeaturedVideo || cinemagraphUrl;
  // When the cinemagraph plays, use the featured still as the poster so the
  // first paint is instant while the clip buffers.
  const posterUrl = !slotIsVideo && videoSrc ? bgImage.image || undefined : undefined;

  // Parallax refs
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  // Google Fonts — match the Aura at Westchase hero: Cardo (serif title) +
  // Inter (sans eyebrow/subtitle). Scoped to the hero via .hero-title-font /
  // .hero-subtitle-font below so the rest of the theme is untouched.
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="hero-aura"]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Cardo:wght@400;700&family=Inter:wght@300;400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'hero-aura');
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      // rect.top < 0 once the section has scrolled above the viewport top
      const scrolled = -rect.top;
      // Only compute while the section is at least partially visible
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      // Shift the image down at 30 % of scroll speed, creating the parallax depth
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed the position on mount (handles pre-scrolled pages)
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section ref={sectionRef} className="relative w-full h-screen overflow-hidden">
      {/* Parallax + Ken Burns animated background */}
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
        {/* Oversized wrapper: 140 % tall starting 20 % above the section top,
            and ~2 % wider than the section (1 % bleed on each side). The
            extra height gives the parallax translate room to move, and the
            extra width absorbs the Ken Burns horizontal translate (-0.8 %)
            so no gap is exposed on the right edge. */}
        <div ref={imgWrapRef} className="absolute h-[140%]" style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}>
          {videoSrc ? (
            <video
              src={videoSrc}
              poster={posterUrl}
              autoPlay
              muted
              loop
              playsInline
              aria-label={bgImage.alt || ''}
              className="w-full h-full object-cover"
              style={{ willChange: 'transform' }}
            />
          ) : bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              sizes="100vw"
              priority
              className="w-full h-full object-cover"
              style={{
                animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                willChange: 'transform',
                transformOrigin: 'bottom right',
                imageRendering: 'high-quality' as CSSProperties['imageRendering'],
              }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Black overlay for text contrast — purely decorative, must not
          intercept clicks (otherwise floating chatbot iframes / widgets
          rendered above the hero can't be clicked through the empty overlay
          area). */}
      <div
        className="absolute inset-0 pointer-events-none bg-black"
        style={{ opacity: 0.45 }}
      />

      {/* Content. The flex container fills the section (h-full) so children
          can be vertically centered, but we disable pointer events on the
          wrapper itself and re-enable them only on the actual interactive
          children. That way empty space in the hero passes clicks through
          to anything floating on top (e.g. the RentCafe CRM IQ chatbot). */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8 pointer-events-none">
        {/* Subtitle sits on top of the title, matching the reference hero. */}
        <RichTextContent
          path="hero.subtitle"
          label="Subtitle"
          defaultValue={subtitleDefault}
          as="p"
          className="hero-entrance hero-entrance-1 hero-subtitle-font text-sm sm:text-base md:text-lg tracking-[0.25em] uppercase font-semibold opacity-90 mb-3 drop-shadow-md pointer-events-auto"
        />
        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-2 hero-title-font text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold max-w-5xl leading-[1.1] tracking-tight drop-shadow-lg uppercase pointer-events-auto"
        />
      </div>

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.1s; }
        .hero-entrance-2 { animation-delay: 0.3s; }
        .hero-title-font { font-family: "Cardo", Georgia, "Times New Roman", serif; }
        .hero-subtitle-font { font-family: "Inter", system-ui, sans-serif; }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_bedrooms_range"></span> Bedroom Apartments For Rent in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
